import type { Facture, FactureLigne, Client } from "../../lib/facturation";
import type { DossierInfos } from "../../lib/dossiers";
import { labelRegimeFiscalClient, regimeFiscalEffectifClient } from "../../lib/fiscalite/airsi";
import { getMoyenPaiementLabel, FACTURE_TRANSLATIONS } from "../../lib/facture-i18n";
import { getUniteLabel } from "../../lib/unites";
import { formatMontant, formatMontantFCFA } from "@/lib/formatMontant";

type TFacture = (typeof FACTURE_TRANSLATIONS)["fr"];

const fmt = (n: number) => formatMontant(n);
const fmtFcfa = (n: number) => formatMontantFCFA(n);
const isAvoir = (f: Facture & { facture_origine_id?: string | null; facture_origine_numero?: string | null }) =>
  f.facture_origine_id != null || (f.facture_origine_numero != null && String(f.facture_origine_numero).trim() !== "");
const fmtDate = (d: string) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

interface FactureApercuProps {
  facture: Facture & { clients?: Client };
  infos: DossierInfos | null;
  client: Client | null;
  lignes: FactureLigne[];
  t: TFacture;
}

export default function FactureApercu({ facture, infos, client, lignes, t }: FactureApercuProps) {
  const moyensPaiement = Array.isArray(facture.moyens_paiement) ? facture.moyens_paiement : facture.mode_paiement ? [facture.mode_paiement] : [];
  const taxesByRate = lignes.reduce<Record<number, { ht: number; tva: number }>>((acc, l) => {
    const taux = Number(l.taux_tva) || 0;
    const ht = Number(l.total_ht) || 0;
    const tva = Number(l.total_tva) || 0;
    if (!acc[taux]) acc[taux] = { ht: 0, tva: 0 };
    acc[taux].ht += ht;
    acc[taux].tva += tva;
    return acc;
  }, {});
  const avoir = isAvoir(facture);
  const hasMontants = (facture.montant_ht != null && Number(facture.montant_ht) !== 0) || (facture.montant_tva != null && Number(facture.montant_tva) !== 0);
  const taxesByRateFallback: Record<number, { ht: number; tva: number }> = {};
  if (Object.keys(taxesByRate).length === 0 && hasMontants) {
    taxesByRateFallback[0] = { ht: Number(facture.montant_ht) || 0, tva: Number(facture.montant_tva) || 0 };
  }
  const taxesToShow = Object.keys(taxesByRate).length > 0 ? taxesByRate : taxesByRateFallback;
  const tauxTvaUniques = Object.keys(taxesToShow)
    .map(Number)
    .filter((x) => !Number.isNaN(x));
  const libelleTvaBloc =
    tauxTvaUniques.length === 1 ? `${t.tva} ${tauxTvaUniques[0]}%` : t.tva;
  const montantAirsi = Number(facture.montant_airsi) || 0;
  const tauxAirsiFacture = Number(facture.taux_airsi) || 0;
  const afficherLigneAirsi = Math.abs(montantAirsi) > 0.0001;
  const nccEmetteur = infos?.ncc || infos?.niu || "";
  const rccmEmetteur = infos?.rccm || infos?.rc || "";
  const adresseEmetteur = [infos?.adresse_postale, infos?.ville].filter(Boolean).join(", ");
  const dateHeureEmission = facture.date_emission ? `${fmtDate(facture.date_emission)} ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : fmtDate(facture.date_emission);

  return (
    <div
      className="facture-apercu"
      style={{
        background: "white",
        border: "1px solid #E0D5C8",
        borderRadius: "12px",
        padding: "32px 40px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px", alignItems: "flex-start" }}>
        <div style={{ border: "1px solid #EAE0D5", borderRadius: "10px", padding: "20px 24px", background: "#FAF6F1" }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#9A8878", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t.emetteur}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "12px" }}>
            {infos?.logo_url ? (
              <img src={infos.logo_url} alt="Logo" style={{ maxHeight: 56, maxWidth: 140, objectFit: "contain" }} />
            ) : (
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#1A0A00" }}>{infos?.denomination || "Entreprise"}</div>
            )}
          </div>
          <div style={{ fontSize: "0.9rem", lineHeight: 1.7, color: "#1A0A00" }}>
            {nccEmetteur && <div>NCC : {nccEmetteur}</div>}
            {infos?.regime_imposition && <div>Régime d&apos;imposition : {infos.regime_imposition}</div>}
            {infos?.centre_impots && <div>Centre des impôts : {infos.centre_impots}</div>}
            <div style={{ marginTop: "20px" }}>
              {rccmEmetteur && <div>RCCM : {rccmEmetteur}</div>}
              {infos?.references_bancaires && <div>Références bancaires : {infos.references_bancaires}</div>}
              {infos?.siege_social && <div>Établissement : {infos.siege_social}</div>}
              {adresseEmetteur && <div>Adresse : {adresseEmetteur}</div>}
              {infos?.telephone && <div>N° Tel : {infos.telephone}</div>}
              {infos?.email && <div>Mail : {infos.email}</div>}
              {infos?.vendeur_nom && <div>Nom du vendeur : {infos.vendeur_nom}</div>}
              {infos?.point_vente && <div>Nom de PDV : {infos.point_vente}</div>}
              <div>Date et heure : {dateHeureEmission}</div>
              {facture.mode_paiement && <div>Mode de paiement : {facture.mode_paiement}</div>}
            </div>
          </div>
          {infos?.formule_politesse && (
            <div style={{ marginTop: "12px", fontStyle: "italic", color: "#5A4A3A", fontSize: "0.9rem" }}>{infos.formule_politesse}</div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
              {facture.numero_fne && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginBottom: "2px", transform: "translateX(-45px)" }}>
                  <div style={{ width: 80, flexShrink: 0 }} />
                  <div style={{ fontSize: "0.8rem", fontWeight: 800, color: "#1A0A00", letterSpacing: "0.02em", textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                    <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, marginRight: "4px" }}>{facture.facture_origine_numero ? "Facture d'avoir N°" : "Facture de vente N°"}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace" }}>{facture.numero_fne}</span>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
                <div style={{ width: 80, height: 80, background: "#F5F0E8", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", color: "#9A8878", flexShrink: 0 }}>QR</div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <img src={infos?.logo_fne_url || "/cdilogo.png"} alt="FNE" style={{ width: 72, height: 72, objectFit: "contain" }} onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  <div style={{ fontSize: "0.6rem", fontWeight: 700, color: "#16A34A", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.fne}</div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ border: "1px solid #EAE0D5", borderRadius: "10px", padding: "20px 24px", background: "#FAF6F1" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#9A8878", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t.client_recepteur}</div>
            <div style={{ fontSize: "0.95rem", lineHeight: 1.7, color: "#1A0A00" }}>
              <div style={{ fontWeight: 700, marginBottom: "6px" }}>{client?.nom || "—"}</div>
              {(client?.adresse || client?.email) && <div>{client.adresse || client.email}</div>}
              {client?.ncc && <div>NCC : {client.ncc}</div>}
              <div>Régime fiscal : {labelRegimeFiscalClient(regimeFiscalEffectifClient(client || {}))}</div>
            </div>
            <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: "1px solid #EAE0D5", fontSize: "0.9rem", fontWeight: 700, color: "#5A4A3A" }}>
              {facture.facture_origine_numero ? "Avoir N°" : t.facture_numerotee} {facture.numero}
            </div>
          </div>
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", marginBottom: "24px" }}>
        <thead>
          <tr style={{ background: "#FAF6F1", borderBottom: "2px solid #EAE0D5" }}>
            <th style={{ padding: "12px 10px", textAlign: "left", fontWeight: 700, color: "#9A8878" }}>{t.ref}</th>
            <th style={{ padding: "12px 10px", textAlign: "left", fontWeight: 700, color: "#9A8878" }}>{t.designation}</th>
            <th style={{ padding: "12px 10px", textAlign: "right", fontWeight: 700, color: "#9A8878" }}>{t.pu_ht}</th>
            <th style={{ padding: "12px 10px", textAlign: "center", fontWeight: 700, color: "#9A8878" }}>{t.qte}</th>
            <th style={{ padding: "12px 10px", textAlign: "left", fontWeight: 700, color: "#9A8878" }}>{t.unite}</th>
            <th style={{ padding: "12px 10px", textAlign: "center", fontWeight: 700, color: "#9A8878" }}>{t.tva} (%)</th>
            <th style={{ padding: "12px 10px", textAlign: "right", fontWeight: 700, color: "#9A8878" }}>{t.total_ht}</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.id} style={{ borderBottom: "1px solid #F5F0E8" }}>
              <td style={{ padding: "12px 10px", color: "#5A4A3A" }}>{l.reference || "—"}</td>
              <td style={{ padding: "12px 10px", fontWeight: 500 }}>{l.libelle}</td>
              <td style={{ padding: "12px 10px", textAlign: "right" }}>{fmt(l.prix_unitaire_ht)}</td>
              <td style={{ padding: "12px 10px", textAlign: "center" }}>{l.quantite}</td>
              <td style={{ padding: "12px 10px", color: "#5A4A3A" }}>{getUniteLabel(l.unite)}</td>
              <td style={{ padding: "12px 10px", textAlign: "center" }}>({l.taux_tva})</td>
              <td style={{ padding: "12px 10px", textAlign: "right", fontWeight: 600, color: avoir && Number(l.total_ht) < 0 ? "#DC2626" : undefined }}>{fmt(Number(l.total_ht) || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px", marginBottom: "24px", maxWidth: 320, marginLeft: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 0" }}>
          <span style={{ color: "#9A8878" }}>{t.total_ht.toUpperCase()}</span>
          <span style={{ fontWeight: 700, color: avoir && Number(facture.montant_ht) < 0 ? "#DC2626" : undefined }}>{fmtFcfa(Number(facture.montant_ht) || 0)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 0" }}>
          <span style={{ color: "#9A8878" }}>{libelleTvaBloc}</span>
          <span style={{ fontWeight: 700, color: avoir && Number(facture.montant_tva) < 0 ? "#DC2626" : undefined }}>{fmtFcfa(Number(facture.montant_tva) || 0)}</span>
        </div>
        {afficherLigneAirsi && (
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 0" }}>
            <span style={{ color: "#9A8878" }}>{`AIRSI ${tauxAirsiFacture || "—"}%`}</span>
            <span
              style={{
                fontWeight: 700,
                color: avoir && montantAirsi < 0 ? "#DC2626" : undefined,
              }}
            >
              {fmtFcfa(montantAirsi)}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "12px 0", borderTop: "2px solid #EAE0D5" }}>
          <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>{t.total_ttc.toUpperCase()}</span>
          <span style={{ fontWeight: 800, fontSize: "1.1rem", color: avoir && Number(facture.montant_ttc) < 0 ? "#DC2626" : "#F97316" }}>{fmtFcfa(Number(facture.montant_ttc) || 0)}</span>
        </div>
        {!afficherLigneAirsi && (
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 0" }}>
            <span style={{ color: "#9A8878" }}>{t.autres_taxes}</span>
            <span style={{ fontWeight: 700 }}>{formatMontantFCFA(0)}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "12px 0", borderTop: "1px solid #EAE0D5", fontSize: "1.05rem" }}>
          <span style={{ fontWeight: 800 }}>{t.total_a_payer}</span>
          <span style={{ fontWeight: 800, color: avoir && Number(facture.montant_ttc) < 0 ? "#DC2626" : "#1A0A00" }}>{fmtFcfa(Number(facture.montant_ttc) || 0)}</span>
        </div>
      </div>
      {Object.keys(taxesToShow).length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#9A8878", marginBottom: "10px" }}>{t.resume_taxes}</div>
          <table style={{ width: "100%", maxWidth: 480, borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #EAE0D5" }}>
                <th style={{ padding: "8px 0", textAlign: "left", fontWeight: 600, color: "#9A8878" }}>{t.categorie}</th>
                <th style={{ padding: "8px 0", textAlign: "right", fontWeight: 600, color: "#9A8878" }}>{t.sous_total}</th>
                <th style={{ padding: "8px 0", textAlign: "center", fontWeight: 600, color: "#9A8878" }}>{t.taux}</th>
                <th style={{ padding: "8px 0", textAlign: "right", fontWeight: 600, color: "#9A8878" }}>{t.total_taxes}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(taxesToShow).map(([taux, { ht, tva }]) => (
                <tr key={taux} style={{ borderBottom: "1px solid #F5F0E8" }}>
                  <td style={{ padding: "8px 0" }}>{t.tva} {taux}%</td>
                  <td style={{ padding: "8px 0", textAlign: "right", color: avoir && ht < 0 ? "#DC2626" : undefined }}>{fmt(ht)}</td>
                  <td style={{ padding: "8px 0", textAlign: "center" }}>{taux}%</td>
                  <td style={{ padding: "8px 0", textAlign: "right", fontWeight: 600, color: avoir && tva < 0 ? "#DC2626" : undefined }}>{fmt(tva)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: "28px", paddingTop: "20px", borderTop: "1px solid #F5F0E8", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
        {facture.facture_origine_numero && (
          <div style={{ textAlign: "center", width: "100%", fontSize: "0.9rem", color: "#166534", fontWeight: 600, marginBottom: "8px" }}>
            Mention spéciale : concerne la facture N° {facture.facture_origine_numero}
          </div>
        )}
        {infos?.phrase_bas_page && (
          <div style={{ textAlign: "center", width: "100%", fontSize: "0.95rem", color: "#5A4A3A", fontStyle: "italic" }}>{infos.phrase_bas_page}</div>
        )}
        {facture.devis_numero && (
          <div style={{ fontSize: "0.85rem", color: "#9A8878", fontStyle: "italic" }}>{t.a_partir_devis} {facture.devis_numero}</div>
        )}
        {moyensPaiement.length > 0 && (
          <div style={{ marginTop: "12px", padding: "16px 20px", border: "1px solid #EAE0D5", borderRadius: "10px", background: "#FAF6F1", minWidth: 240 }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#9A8878", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.moyen_paiement}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {moyensPaiement.map((m) => (
                <span key={m} style={{ padding: "6px 12px", background: "white", borderRadius: "6px", fontSize: "0.85rem", fontWeight: 600, color: "#1A0A00", border: "1px solid #E5E0D8" }}>
                  {getMoyenPaiementLabel(m, t)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
